import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Menu, X, KeyRound } from "lucide-react";
import { hasAccess, roleLabel } from "@/lib/roles";
import { useVisibility, canSee } from "@/lib/visibility";

const links = [
  { to: "/dashboard", label: "Dashboard", roles: ["admin", "gm", "manager", "employee"] },
  { to: "/opening", label: "Opening", roles: ["admin", "manager", "employee"] },
  { to: "/closing", label: "Closing", roles: ["admin", "manager", "employee"] },
  { to: "/schedules", label: "Schedules", roles: ["admin", "gm", "manager", "employee"], feature: "schedule" },
  { to: "/schedules/week", label: "Weekly", roles: ["admin", "gm", "manager", "employee"], feature: "weekly" },
  { to: "/shift-templates", label: "Templates", roles: ["admin", "gm", "manager"], feature: "template" },
  { to: "/display", label: "Display", roles: ["admin", "gm", "global_standards", "manager"] },
  { to: "/float", label: "Float Log", roles: ["admin", "gm", "manager"] },
  { to: "/receiving", label: "Receiving", roles: ["admin", "gm", "manager"] },
  { to: "/damages", label: "Damages", roles: ["admin", "gm", "manager", "assistant_manager", "inventory_manager", "employee", "zone_owner", "cashier", "inventory_assistant"] },
  { to: "/global-standards", label: "Standards", roles: ["admin", "global_standards"] },
  { to: "/requests", label: "Requests", roles: ["admin", "gm", "manager", "employee"] },
  { to: "/reports", label: "Reports", roles: ["admin", "gm", "manager", "employee"] },
  { to: "/issues", label: "Issues", roles: ["admin", "gm", "manager", "employee"] },
  { to: "/admin", label: "Admin", roles: ["admin"] },
];

export default function TopNav() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const vis = useVisibility(user?.id);
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const visible = links.filter((l) => hasAccess(user.role, l.roles) && (!l.feature || canSee(vis?.my_access, l.feature)));

  return (
    <header className="sticky top-0 z-40 border-b border-[color:var(--border)] bg-[color:var(--bg-surface)]/85 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <img src="/hos-logo.webp" alt="House of Spells" className="h-9 w-9 object-contain" />
          <div className="leading-tight whitespace-nowrap">
            <div className="font-heading text-xl text-[color:var(--text-primary)]">House of Spells</div>
            <div className="label-caps text-[10px]">Times Square</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-0.5 flex-1 justify-center overflow-x-auto">
          {visible.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`px-2.5 py-2 rounded-md text-sm font-medium transition whitespace-nowrap ${
                loc.pathname.startsWith(l.to)
                  ? "text-[color:var(--gold)] bg-[color:var(--bg-surface-2)]"
                  : "text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-2)]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <div className="text-right leading-tight max-w-[140px] truncate">
            <div className="text-sm text-[color:var(--text-primary)] truncate" data-testid="nav-user-name">{user.name}</div>
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gold)] truncate">{roleLabel(user.role)}</div>
          </div>
          <Link
            to="/account/password"
            data-testid="nav-change-password"
            title="Change password"
            className="p-2 rounded-md hover:bg-[color:var(--bg-surface-2)] text-[color:var(--text-secondary)] hover:text-[color:var(--gold)]"
          >
            <KeyRound size={18} />
          </Link>
          <button
            data-testid="nav-logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="p-2 rounded-md hover:bg-[color:var(--bg-surface-2)] text-[color:var(--text-secondary)] hover:text-[color:var(--gold)]"
          >
            <LogOut size={18} />
          </button>
        </div>

        <button className="md:hidden text-[color:var(--gold)]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[color:var(--border)] bg-[color:var(--bg-surface)]">
          <div className="px-4 py-3 flex flex-col gap-1">
            {visible.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className={`px-3 py-3 rounded-md text-sm font-medium ${
                  loc.pathname.startsWith(l.to)
                    ? "text-[color:var(--gold)] bg-[color:var(--bg-surface-2)]"
                    : "text-[color:var(--text-secondary)]"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/account/password"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-change-password"
              className="px-3 py-3 rounded-md text-sm font-medium text-[color:var(--text-secondary)]"
            >
              Change Password
            </Link>
            <button
              data-testid="nav-mobile-logout"
              className="mt-2 px-3 py-3 rounded-md text-left text-sm text-[color:var(--error)]"
              onClick={async () => { await logout(); nav("/login"); }}
            >
              Log out
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
